import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import ApiUrl from '../../utils/config';
import { NotificationType } from '../../common/types';
import AppContext from '../../AppContext';
import { handleError } from '../../utils';
import { Tournament, TournamentClient } from '../../utils/apiClient';
import { fetchCategoryOverviews, TournamentCategoryOverview } from '../../utils/categoryOverviewApi';
import {
  fetchMatDisplaySettings,
  MatDisplaySetting,
  MatIdleMode,
  updateMatDisplaySetting,
} from '../../utils/matDisplayApi';
import './MatControlPage.scss';

const MATS = [1, 2, 3, 4];
const emptySetting = (matNumber: number): MatDisplaySetting => ({
  matNumber,
  idleMode: 'Wait',
  tournamentId: undefined,
  ageCategoryInTournamentId: undefined,
  weightCategoryInTournamentId: undefined,
});

const categoryKey = (ageId?: number | null, weightId?: number | null) =>
  ageId && weightId ? `${ageId}-${weightId}` : '';

const MatControlPage: React.FC = () => {
  const appContext = useContext(AppContext);
  const tournamentClient = useMemo(() => new TournamentClient(ApiUrl, axios), []);

  const [loading, setLoading] = useState(true);
  const [savingMat, setSavingMat] = useState<number | null>(null);
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [settings, setSettings] = useState<Record<number, MatDisplaySetting>>({});
  const [overviewsByTournament, setOverviewsByTournament] = useState<
    Record<number, TournamentCategoryOverview[]>
  >({});

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const [saved, tournamentList] = await Promise.all([
          fetchMatDisplaySettings(),
          tournamentClient.getTournaments(),
        ]);
        if (cancelled) return;
        const next: Record<number, MatDisplaySetting> = {};
        for (const n of MATS) {
          next[n] = saved.find(s => s.matNumber === n) ?? emptySetting(n);
        }
        setSettings(next);
        setTournaments(tournamentList);

        const tournamentIds = [...new Set(
          saved.map(s => s.tournamentId).filter((id): id is number => !!id)
        )];
        const overviewEntries = await Promise.all(
          tournamentIds.map(async id => [id, await fetchCategoryOverviews(id)] as const)
        );
        if (cancelled) return;
        setOverviewsByTournament(Object.fromEntries(overviewEntries));
      } catch (ex) {
        if (!cancelled) {
          handleError(appContext!.setNotificationData, ex, 'Не удалось загрузить настройки табло');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [tournamentClient]);

  const ensureOverviews = async (tournamentId: number) => {
    if (overviewsByTournament[tournamentId]) return;
    try {
      const list = await fetchCategoryOverviews(tournamentId);
      setOverviewsByTournament(prev => ({ ...prev, [tournamentId]: list }));
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex, 'Не удалось загрузить категории турнира');
    }
  };

  const patchMat = (matNumber: number, patch: Partial<MatDisplaySetting>) => {
    setSettings(prev => ({
      ...prev,
      [matNumber]: { ...prev[matNumber], ...patch },
    }));
  };

  const handleTournamentChange = async (matNumber: number, raw: string) => {
    const tournamentId = raw ? Number(raw) : undefined;
    patchMat(matNumber, {
      tournamentId,
      ageCategoryInTournamentId: undefined,
      weightCategoryInTournamentId: undefined,
    });
    if (tournamentId) await ensureOverviews(tournamentId);
  };

  const handleSave = async (matNumber: number) => {
    const current = settings[matNumber];
    if (!current) return;
    setSavingMat(matNumber);
    try {
      const saved = await updateMatDisplaySetting({
        ...current,
        tournamentId: current.idleMode === 'Wait' ? undefined : current.tournamentId,
      });
      patchMat(matNumber, saved);
      appContext?.setDisappearingNotification({
        message: `Ковёр ${matNumber}: настройки сохранены`,
        useAbsolute: true,
      });
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex, 'Не удалось сохранить настройки ковра');
    } finally {
      setSavingMat(null);
    }
  };

  if (loading) {
    return <div className="mat-control">Загрузка пульта табло…</div>;
  }

  return (
    <div className="mat-control">
      <div className="mat-control__header">
        <div>
          <h2>Табло зала</h2>
          <p>
            Во время боя экран всегда показывает счёт. Здесь выбирается, что крутить
            в паузе: ожидание, ближайшие поединки или сетку/таблицу группы.
          </p>
        </div>
      </div>

      <div className="mat-control__grid">
        {MATS.map(matNumber => {
          const setting = settings[matNumber] ?? emptySetting(matNumber);
          const overviews = setting.tournamentId
            ? overviewsByTournament[setting.tournamentId] ?? []
            : [];
          const approved = overviews.filter(o => o.isApproved && o.mainBracketId);
          const categoryOptions = setting.idleMode === 'Bracket' ? approved : overviews.filter(o => o.totalParticipants > 0);
          const selectedKey = categoryKey(
            setting.ageCategoryInTournamentId,
            setting.weightCategoryInTournamentId
          );
          const needsTournament = setting.idleMode !== 'Wait';
          const needsCategory = setting.idleMode === 'Bracket';
          const canSave =
            !needsTournament
            || (!!setting.tournamentId && (!needsCategory || !!selectedKey));

          return (
            <section key={matNumber} className="mat-control__card">
              <header className="mat-control__card-head">
                <h3>Ковёр {matNumber}</h3>
                <a href={`/display/${matNumber}`} target="_blank" rel="noreferrer">
                  Открыть /display/{matNumber}
                </a>
              </header>

              <label className="mat-control__label">Между боями</label>
              <div className="mat-control__modes">
                {([
                  ['Wait', 'Ожидание'],
                  ['Upcoming', 'Ближайшие поединки'],
                  ['Bracket', 'Сетка / таблица'],
                ] as [MatIdleMode, string][]).map(([mode, label]) => (
                  <label key={mode} className={setting.idleMode === mode ? 'is-active' : ''}>
                    <input
                      type="radio"
                      name={`mode-${matNumber}`}
                      checked={setting.idleMode === mode}
                      onChange={() => patchMat(matNumber, { idleMode: mode })}
                    />
                    {label}
                  </label>
                ))}
              </div>

              {needsTournament && (
                <>
                  <label className="mat-control__label" htmlFor={`t-${matNumber}`}>Турнир</label>
                  <select
                    id={`t-${matNumber}`}
                    value={setting.tournamentId ?? ''}
                    onChange={(e) => handleTournamentChange(matNumber, e.target.value)}
                  >
                    <option value="">Выберите турнир</option>
                    {tournaments.map(t => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </>
              )}

              {needsTournament && setting.tournamentId && (
                <>
                  <label className="mat-control__label" htmlFor={`c-${matNumber}`}>
                    {needsCategory ? 'Возраст и вес' : 'Категория (необязательно)'}
                  </label>
                  <select
                    id={`c-${matNumber}`}
                    value={selectedKey}
                    onChange={(e) => {
                      const [age, weight] = e.target.value
                        ? e.target.value.split('-').map(Number)
                        : [undefined, undefined];
                      patchMat(matNumber, {
                        ageCategoryInTournamentId: age,
                        weightCategoryInTournamentId: weight,
                      });
                    }}
                  >
                    <option value="">
                      {needsCategory ? 'Выберите группу' : 'Все категории турнира'}
                    </option>
                    {categoryOptions.map(o => (
                      <option
                        key={`${o.ageCategoryInTournamentId}-${o.weightCategoryInTournamentId}`}
                        value={`${o.ageCategoryInTournamentId}-${o.weightCategoryInTournamentId}`}
                      >
                        {o.ageCategoryName} · {o.weightCategoryLabel}
                        {o.format ? ` (${o.format === 'RoundRobin' ? 'круговая' : o.format === 'Mixed' ? 'смешанная' : 'олимпийская'})` : ''}
                      </option>
                    ))}
                  </select>
                </>
              )}

              <button
                type="button"
                className="mat-control__save"
                disabled={savingMat === matNumber || !canSave}
                onClick={() => handleSave(matNumber)}
              >
                {savingMat === matNumber ? 'Сохранение…' : 'Показать на табло'}
              </button>
            </section>
          );
        })}
      </div>

      <p className="mat-control__hint">
        Если судья открыл пару, но ещё не нажал старт, табло покажет фамилии.
        Идущий поединок этим экраном сменить нельзя.
      </p>
      <p className="mat-control__back">
        <Link to="/admin">К панели администратора</Link>
      </p>
    </div>
  );
};

export default MatControlPage;
